// ============================================================================
// SessionsPanel — this client's sessions, split into UPCOMING (planned, in the
// future) and PAST (completed / elapsed). Each row links to the session detail
// route; the header offers a "schedule session" entry point. The full session-
// logging UI is a later page, so these are navigational links only.
// Data: useClientSessions (live-synced). Optional-first: a missing time just
// shows the status; an empty list shows an encouraging empty state.
// ============================================================================
import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardHeader, EmptyState } from "../../components";
import { IconCalendar, IconClock, IconPlus } from "../../components/icons";
import { useClientSessions } from "../../hooks/sessions";
import type { Session } from "../../api";
import { LoadingBlock } from "../../app/PageState";
import { cn } from "../../lib/cn";
import { fmtDateTime } from "./lib";

interface SessionsPanelProps {
  clientId: number;
}

function sessionWhen(s: Session): number {
  const iso = s.performed_at ?? s.scheduled_at;
  if (!iso) return 0;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

export function SessionsPanel({ clientId }: SessionsPanelProps) {
  const { t } = useTranslation("clientDetail");
  const query = useClientSessions(clientId);

  const { upcoming, past } = useMemo(() => {
    const all = query.data ?? [];
    const now = Date.now();
    const up: Session[] = [];
    const pa: Session[] = [];
    for (const s of all) {
      const when = sessionWhen(s);
      if (s.status === "planned" && (when === 0 || when >= now)) up.push(s);
      else pa.push(s);
    }
    up.sort((a, b) => sessionWhen(a) - sessionWhen(b)); // soonest first
    pa.sort((a, b) => sessionWhen(b) - sessionWhen(a)); // most recent first
    return { upcoming: up, past: pa };
  }, [query.data]);

  return (
    <Card>
      <CardHeader
        title={t("sessions.title")}
        subtitle={t("sessions.subtitle")}
        action={
          <Link to={`/app/sessions?client=${clientId}`}>
            <Button size="sm" iconLeft={<IconPlus width={16} height={16} />}>
              {t("sessions.schedule")}
            </Button>
          </Link>
        }
      />

      {query.isLoading ? (
        <LoadingBlock />
      ) : upcoming.length === 0 && past.length === 0 ? (
        <EmptyState
          title={t("sessions.emptyTitle")}
          description={t("sessions.emptyBlurb")}
          action={
            <Link to={`/app/sessions?client=${clientId}`}>
              <Button size="sm" iconLeft={<IconPlus width={16} height={16} />}>
                {t("sessions.schedule")}
              </Button>
            </Link>
          }
          className="!py-12"
        />
      ) : (
        <div className="space-y-5">
          {upcoming.length > 0 && (
            <Group title={t("sessions.upcoming", { count: upcoming.length })}>
              {upcoming.map((s, i) => (
                <SessionRow key={s.id} session={s} index={i} />
              ))}
            </Group>
          )}
          {past.length > 0 && (
            <Group title={t("sessions.past", { count: past.length })}>
              {past.slice(0, 8).map((s, i) => (
                <SessionRow key={s.id} session={s} index={i} />
              ))}
            </Group>
          )}
        </div>
      )}
    </Card>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
        {title}
      </p>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function SessionRow({ session, index }: { session: Session; index: number }) {
  const { t } = useTranslation("clientDetail");
  const reduce = useReducedMotion();
  const isPlanned = session.status === "planned";
  const when = session.performed_at ?? session.scheduled_at;
  const location = session.location_text;

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: reduce ? 0 : Math.min(index * 0.04, 0.3) }}
    >
      <Link
        to={`/app/sessions/${session.id}`}
        className={cn(
          "flex items-center gap-3 rounded-md p-3 ring-1 transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm",
          isPlanned ? "bg-coral-soft/40 ring-coral/20" : "bg-surface ring-line",
        )}
      >
        <span
          className={cn(
            "grid h-10 w-10 shrink-0 place-items-center rounded-md text-white",
            isPlanned ? "bg-grad-coral" : "bg-grad-ocean",
          )}
        >
          {isPlanned ? (
            <IconCalendar width={18} height={18} />
          ) : (
            <IconClock width={18} height={18} />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {when ? fmtDateTime(when) : t("sessions.undated")}
            </span>
            <Badge tone={isPlanned ? "volt" : "success"}>
              {t(isPlanned ? "sessions.planned" : "sessions.completed")}
            </Badge>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-body-soft">
            {session.type && <span className="truncate">{session.type}</span>}
            {session.duration_min != null && (
              <span>{t("sessions.duration", { min: session.duration_min })}</span>
            )}
            {location && <span className="truncate">{location}</span>}
          </div>
        </div>

        <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-body-faint" fill="none" aria-hidden>
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
    </motion.li>
  );
}
